<template>
  <SettingsSection>
    <SettingsSectionTitle> Gratuity </SettingsSectionTitle>

    <SettingsSubsection>
      <form
        :class="[
          isMobile ? 'grid-cols-1' : 'grid-cols-2',
          'grid gap-2',
        ]"
        @submit="onSubmit"
      >
        <div class="flex flex-col gap-3">
          <p
            class="text-background-fg/50 mt-1 text-xs font-medium"
            >Set the percentage to 0 to disable. The maximum
            gratuity percentage is 50%</p
          >
          <FormInputPercentage
            v-for="index in [0, 1, 2, 3]"
            :key="index"
            :name="`gratuity${index + 1}Amount`"
            :label="`Gratuity option ${index + 1}`"
          />
          <div class="mt-4 flex justify-end">
            <FormSubmitButton> Save </FormSubmitButton>
          </div>
        </div>

        <GratuityDrawerPreview
          v-if="!isMobile"
          class="m-4 h-[568px]"
          :gratuity-options-list="gratuityOptionsList"
        />
      </form>
      <BaseInnerLoading v-if="isSubmitting" />
    </SettingsSubsection>
  </SettingsSection>
</template>

<script setup lang="ts">
import z from "zod"
import { useForm } from "vee-validate"
import { toTypedSchema } from "@vee-validate/zod"
import type { TypedSchemaOutput } from "@/utils/utilityTypes"

const isMobile = useIsMobile()

const gratuityAmountScema = z
  .number()
  .max(50, "Gratuity can't be more than 50%")
  .min(0, "Gratuity percentage can't be negative")

const gratuitySettingsFormSchema = z.object({
  gratuity1Amount: gratuityAmountScema,
  gratuity2Amount: gratuityAmountScema,
  gratuity3Amount: gratuityAmountScema,
  gratuity4Amount: gratuityAmountScema,
})

const { data: gratuitySettings } = useGratuityOptions()

export type SettingsGratuityFormOutput = TypedSchemaOutput<
  typeof gratuitySettingsFormSchema
>

const {
  handleSubmit,
  setValues,
  values,
  isSubmitting,
  errors,
} = useForm({
  validationSchema: toTypedSchema(
    gratuitySettingsFormSchema
  ),
  initialValues: {
    gratuity1Amount: 0,
    gratuity2Amount: 0,
    gratuity3Amount: 0,
    gratuity4Amount: 0,
  },
})

whenever(
  () => gratuitySettings.value,
  (newGratuitySettings) => {
    setValues({
      gratuity1Amount: newGratuitySettings[0],
      gratuity2Amount: newGratuitySettings[1],
      gratuity3Amount: newGratuitySettings[2],
      gratuity4Amount: newGratuitySettings[3],
    })
  },
  {
    immediate: true,
  }
)

const gratuityOptionsList = computed(() => {
  const fields = [
    "gratuity1Amount",
    "gratuity2Amount",
    "gratuity3Amount",
    "gratuity4Amount",
  ] as const

  const validGratuityOptions = fields.filter(
    (field) => !errors.value[field]
  )

  return validGratuityOptions.map(
    (field) => values[field] ?? 0
  )
})

const onSubmit = handleSubmit(async (params) => {
  try {
    await updateGratuityOptions(params)
    toast.success("Gratuity settings updated")
  } catch (error) {
    toast.error("Failed to update gratuity settings")
    logError(error)
  }
})
</script>
